import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, Typography, IconButton, Grid, Stack, Chip, Switch, FormControlLabel,
  Divider, Alert, CircularProgress, Paper, Tooltip, Snackbar, Card, CardContent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CalculateIcon from '@mui/icons-material/Calculate';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PreviewIcon from '@mui/icons-material/Preview';
import {
  getCommissionConfig,
  updateCommissionConfig,
  deleteCommissionConfig,
  previewCommission
} from '../../services/commissionConfigService';

const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '0 ₫';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(amount);
};

const emptyTimeSlot = { name: '', startTime: '10:00', endTime: '13:00', rate: 2, shopeeRate: '', tiktokRate: '' };
const emptyMegaSaleRule = { name: 'Mega Sale', dates: [], startTime: '23:00', endTime: '01:00', rate: 1, shopeeRate: '', tiktokRate: '' };

// '' (rỗng) → null khi lưu (dùng rate chung); số hợp lệ → number
const toRate = (v) => (v === '' || v === null || v === undefined ? null : (parseFloat(v) || 0));
const fromRate = (v) => (v === null || v === undefined ? '' : v);

const CommissionConfigEditor = ({ open, onClose, user, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    isActive: true,
    defaultRate: 3,
    defaultShopeeRate: '',
    defaultTiktokRate: '',
    timeSlots: [],
    megaSaleRules: []
  });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Preview state
  const [previewData, setPreviewData] = useState({
    totalSales: 5000000,
    startTime: '12:00',
    endTime: '14:00',
    platform: 'shopee',
    sessionDate: new Date().toISOString().split('T')[0]
  });
  const [previewResult, setPreviewResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const fetchConfig = useCallback(async () => {
    if (!user?._id) return;
    setLoading(true);
    try {
      const result = await getCommissionConfig(user._id);
      if (result.success && result.data) {
        setConfig({
          isActive: result.data.isActive ?? true,
          defaultRate: result.data.defaultRate ?? 3,
          defaultShopeeRate: fromRate(result.data.defaultShopeeRate),
          defaultTiktokRate: fromRate(result.data.defaultTiktokRate),
          timeSlots: (result.data.timeSlots || []).map(s => ({
            ...s,
            shopeeRate: fromRate(s.shopeeRate),
            tiktokRate: fromRate(s.tiktokRate)
          })),
          megaSaleRules: (result.data.megaSaleRules || []).map(r => ({
            ...r,
            dates: r.dates || [],
            _dateInput: (r.dates || []).join(', '),
            shopeeRate: fromRate(r.shopeeRate),
            tiktokRate: fromRate(r.tiktokRate)
          }))
        });
      } else {
        setConfig({
          isActive: true,
          defaultRate: user.commissionPercent || 3,
          defaultShopeeRate: '',
          defaultTiktokRate: '',
          timeSlots: [],
          megaSaleRules: []
        });
      }
    } catch (err) {
      showNotification('Lỗi tải cấu hình', 'error');
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (open) {
      fetchConfig();
      setPreviewResult(null);
    }
  }, [open, fetchConfig]);

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const buildCleanConfig = () => ({
    ...config,
    defaultShopeeRate: toRate(config.defaultShopeeRate),
    defaultTiktokRate: toRate(config.defaultTiktokRate),
    timeSlots: config.timeSlots.map(s => ({
      ...s,
      rate: parseFloat(s.rate) || 0,
      shopeeRate: toRate(s.shopeeRate),
      tiktokRate: toRate(s.tiktokRate)
    })),
    megaSaleRules: config.megaSaleRules.map(({ _dateInput, ...rule }) => ({
      ...rule,
      rate: parseFloat(rule.rate) || 0,
      shopeeRate: toRate(rule.shopeeRate),
      tiktokRate: toRate(rule.tiktokRate),
      dates: rule.dates.filter(d => /^\d{2}-\d{2}$/.test(d))
    }))
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanConfig = buildCleanConfig();
      const result = await updateCommissionConfig(user._id, cleanConfig);
      if (result.success) {
        showNotification(result.message || 'Đã lưu cấu hình');
        onSaved?.();
      } else {
        showNotification(result.error?.message || 'Lỗi lưu cấu hình', 'error');
      }
    } catch (err) {
      showNotification('Lỗi server', 'error');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Xóa cấu hình hoa hồng theo giờ? User sẽ dùng flat rate.')) return;
    setSaving(true);
    try {
      const result = await deleteCommissionConfig(user._id);
      if (result.success) {
        showNotification(result.message || 'Đã xóa cấu hình');
        onSaved?.();
        onClose();
      } else {
        showNotification(result.error?.message || 'Lỗi xóa', 'error');
      }
    } catch (err) {
      showNotification('Lỗi server', 'error');
    }
    setSaving(false);
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      // Save first so preview uses latest config
      await updateCommissionConfig(user._id, buildCleanConfig());

      const result = await previewCommission(user._id, previewData);
      if (result.success) {
        setPreviewResult(result.data);
      } else {
        showNotification(result.error?.message || 'Lỗi preview', 'error');
      }
    } catch (err) {
      showNotification('Lỗi server', 'error');
    }
    setPreviewing(false);
  };

  // Time slot handlers
  const addTimeSlot = () => {
    setConfig(prev => ({ ...prev, timeSlots: [...prev.timeSlots, { ...emptyTimeSlot }] }));
  };
  const removeTimeSlot = (index) => {
    setConfig(prev => ({ ...prev, timeSlots: prev.timeSlots.filter((_, i) => i !== index) }));
  };
  const updateTimeSlot = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      timeSlots: prev.timeSlots.map((s, i) => i === index ? { ...s, [field]: field === 'rate' ? (parseFloat(value) || 0) : value } : s)
    }));
  };

  // Mega sale handlers
  const addMegaSaleRule = () => {
    setConfig(prev => ({ ...prev, megaSaleRules: [...prev.megaSaleRules, { ...emptyMegaSaleRule, _dateInput: '' }] }));
  };
  const removeMegaSaleRule = (index) => {
    setConfig(prev => ({ ...prev, megaSaleRules: prev.megaSaleRules.filter((_, i) => i !== index) }));
  };
  const updateMegaSaleRule = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      megaSaleRules: prev.megaSaleRules.map((r, i) => {
        if (i !== index) return r;
        if (field === 'dates') {
          // Parse "04-04, 05-05, 06-06" format
          const dates = value.split(',').map(d => d.trim()).filter(d => d);
          return { ...r, dates, _dateInput: value };
        }
        if (field === 'rate') return { ...r, rate: parseFloat(value) || 0 };
        return { ...r, [field]: value };
      })
    }));
  };

  if (!user) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon color="warning" />
          Cấu hình hoa hồng theo giờ - {user.fullName}
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              {/* Active toggle & Default rate */}
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.isActive}
                        onChange={(e) => setConfig(prev => ({ ...prev, isActive: e.target.checked }))}
                        color="warning"
                      />
                    }
                    label="Kích hoạt hoa hồng theo giờ"
                  />
                  <TextField
                    label="Giờ phụ - chung (%)"
                    type="number"
                    size="small"
                    value={config.defaultRate}
                    onChange={(e) => setConfig(prev => ({ ...prev, defaultRate: parseFloat(e.target.value) || 0 }))}
                    inputProps={{ min: 0, max: 50, step: 0.5 }}
                    sx={{ width: 150 }}
                  />
                  <TextField
                    label="Giờ phụ - Shopee (%)"
                    type="number"
                    size="small"
                    value={config.defaultShopeeRate}
                    onChange={(e) => setConfig(prev => ({ ...prev, defaultShopeeRate: e.target.value }))}
                    inputProps={{ min: 0, max: 50, step: 0.5 }}
                    placeholder="dùng chung"
                    sx={{ width: 160 }}
                  />
                  <TextField
                    label="Giờ phụ - TikTok (%)"
                    type="number"
                    size="small"
                    value={config.defaultTiktokRate}
                    onChange={(e) => setConfig(prev => ({ ...prev, defaultTiktokRate: e.target.value }))}
                    inputProps={{ min: 0, max: 50, step: 0.5 }}
                    placeholder="dùng chung"
                    sx={{ width: 160 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Để trống rate Shopee/TikTok = dùng rate chung. Flat rate hiện tại: {user.commissionPercent || 0}%
                </Typography>
              </Paper>

              {/* Time Slots */}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTimeIcon fontSize="small" color="primary" /> Khung giờ
                  </Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={addTimeSlot} sx={{ textTransform: 'none' }}>
                    Thêm khung giờ
                  </Button>
                </Stack>
                {config.timeSlots.length === 0 && (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Chưa có khung giờ. Tất cả sẽ dùng rate mặc định ({config.defaultRate}%).
                  </Alert>
                )}
                <Stack spacing={1.5}>
                  {config.timeSlots.map((slot, index) => (
                    <Paper key={index} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #bfdbfe', bgcolor: '#eff6ff' }}>
                      <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth size="small" label="Tên"
                            value={slot.name}
                            onChange={(e) => updateTimeSlot(index, 'name', e.target.value)}
                            placeholder="VD: Ca sáng"
                          />
                        </Grid>
                        <Grid item xs={5} sm={2.5}>
                          <TextField
                            fullWidth size="small" label="Bắt đầu" type="time"
                            value={slot.startTime}
                            onChange={(e) => updateTimeSlot(index, 'startTime', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={5} sm={2.5}>
                          <TextField
                            fullWidth size="small" label="Kết thúc" type="time"
                            value={slot.endTime}
                            onChange={(e) => updateTimeSlot(index, 'endTime', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={4} sm={1.3}>
                          <TextField
                            fullWidth size="small" label="Chung %" type="number"
                            value={slot.rate}
                            onChange={(e) => updateTimeSlot(index, 'rate', e.target.value)}
                            inputProps={{ min: 0, max: 50, step: 0.5 }}
                          />
                        </Grid>
                        <Grid item xs={4} sm={1.3}>
                          <TextField
                            fullWidth size="small" label="Shopee %" type="number"
                            value={slot.shopeeRate}
                            onChange={(e) => updateTimeSlot(index, 'shopeeRate', e.target.value)}
                            inputProps={{ min: 0, max: 50, step: 0.5 }}
                            placeholder="—"
                          />
                        </Grid>
                        <Grid item xs={4} sm={1.3}>
                          <TextField
                            fullWidth size="small" label="TikTok %" type="number"
                            value={slot.tiktokRate}
                            onChange={(e) => updateTimeSlot(index, 'tiktokRate', e.target.value)}
                            inputProps={{ min: 0, max: 50, step: 0.5 }}
                            placeholder="—"
                          />
                        </Grid>
                        <Grid item xs={12} sm={0.8}>
                          <Tooltip title="Xóa">
                            <IconButton size="small" color="error" onClick={() => removeTimeSlot(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </Box>

              <Divider />

              {/* Mega Sale Rules */}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocalOfferIcon fontSize="small" color="error" /> Mega Sale Rules
                  </Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={addMegaSaleRule} sx={{ textTransform: 'none' }}>
                    Thêm rule
                  </Button>
                </Stack>
                {config.megaSaleRules.length === 0 && (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Chưa có mega sale rule. Ngày đôi (04-04, 05-05...) sẽ dùng rate thường.
                  </Alert>
                )}
                <Stack spacing={1.5}>
                  {config.megaSaleRules.map((rule, index) => (
                    <Paper key={index} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #fca5a5', bgcolor: '#fef2f2' }}>
                      <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth size="small" label="Tên rule"
                            value={rule.name}
                            onChange={(e) => updateMegaSaleRule(index, 'name', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth size="small" label="Ngày áp dụng (MM-DD)"
                            value={rule._dateInput ?? (rule.dates || []).join(', ')}
                            onChange={(e) => updateMegaSaleRule(index, 'dates', e.target.value)}
                            placeholder="04-04, 05-05, 06-06"
                            helperText="Cách nhau bằng dấu phẩy"
                          />
                        </Grid>
                        <Grid item xs={5} sm={1.5}>
                          <TextField
                            fullWidth size="small" label="Từ" type="time"
                            value={rule.startTime}
                            onChange={(e) => updateMegaSaleRule(index, 'startTime', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={5} sm={1.5}>
                          <TextField
                            fullWidth size="small" label="Đến" type="time"
                            value={rule.endTime}
                            onChange={(e) => updateMegaSaleRule(index, 'endTime', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={4} sm={1}>
                          <TextField
                            fullWidth size="small" label="%" type="number"
                            value={rule.rate}
                            onChange={(e) => updateMegaSaleRule(index, 'rate', e.target.value)}
                            inputProps={{ min: 0, max: 50, step: 0.5 }}
                          />
                        </Grid>
                        <Grid item xs={2} sm={1}>
                          <Tooltip title="Xóa">
                            <IconButton size="small" color="error" onClick={() => removeMegaSaleRule(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </Box>

              <Divider />

              {/* Preview Calculator */}
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalculateIcon fontSize="small" color="warning" /> Preview tính thử
                </Typography>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #fde68a', bgcolor: '#fffbeb' }}>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth size="small" label="Doanh thu" type="number"
                        value={previewData.totalSales}
                        onChange={(e) => setPreviewData(prev => ({ ...prev, totalSales: parseFloat(e.target.value) || 0 }))}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        fullWidth size="small" label="Ngày" type="date"
                        value={previewData.sessionDate}
                        onChange={(e) => setPreviewData(prev => ({ ...prev, sessionDate: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={5} sm={2}>
                      <TextField
                        fullWidth size="small" label="Bắt đầu" type="time"
                        value={previewData.startTime}
                        onChange={(e) => setPreviewData(prev => ({ ...prev, startTime: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={5} sm={2}>
                      <TextField
                        fullWidth size="small" label="Kết thúc" type="time"
                        value={previewData.endTime}
                        onChange={(e) => setPreviewData(prev => ({ ...prev, endTime: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        select fullWidth size="small" label="Nền tảng"
                        SelectProps={{ native: true }}
                        value={previewData.platform}
                        onChange={(e) => setPreviewData(prev => ({ ...prev, platform: e.target.value }))}
                      >
                        <option value="shopee">Shopee</option>
                        <option value="tiktok">TikTok</option>
                        <option value="other">Khác</option>
                      </TextField>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={previewing ? <CircularProgress size={14} color="inherit" /> : <PreviewIcon />}
                        onClick={handlePreview}
                        disabled={previewing}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                      >
                        Tính
                      </Button>
                    </Grid>
                  </Grid>

                  {previewResult && (
                    <Card elevation={0} sx={{ mt: 2, borderRadius: 2, border: '1px solid #a7f3d0', bgcolor: '#ecfdf5' }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Stack direction="row" spacing={3} flexWrap="wrap">
                          <Box>
                            <Typography variant="caption" color="text.secondary">Hoa hồng</Typography>
                            <Typography variant="h6" fontWeight={700} color="success.dark">
                              {formatMoney(previewResult.totalCommission)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Effective rate</Typography>
                            <Typography variant="h6" fontWeight={700} color="warning.dark">
                              {previewResult.effectiveRate}%
                            </Typography>
                          </Box>
                        </Stack>
                        {previewResult.breakdown && previewResult.breakdown.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" fontWeight={600}>Chi tiết:</Typography>
                            {previewResult.breakdown.map((seg, i) => (
                              <Typography key={i} variant="caption" display="block" sx={{ ml: 1 }}>
                                {seg.startTime}-{seg.endTime} ({seg.minutes}p) [{seg.slotName}]: {formatMoney(seg.revenue)} x {seg.rate}% = {formatMoney(seg.commission)}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Paper>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={handleDelete}
            disabled={saving}
            sx={{ textTransform: 'none', mr: 'auto' }}
          >
            Xóa config
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Đóng
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: 'none', borderRadius: 2,
              background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #b45309 0%, #92400e 100%)' }
            }}
          >
            Lưu cấu hình
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={notification.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CommissionConfigEditor;
